/**
 * Per-browser display preferences (theme, display currency, vault auto-lock). Stored in localStorage
 * as a convenience only — wrapped in try/catch because storage can be blocked (private windows).
 */
import { DEFAULT_AUTO_LOCK_MINUTES, isAutoLockMinutes, type AutoLockMinutes } from "@custos/vault";
import { create } from "zustand";

export type ThemePref = "system" | "dark" | "light";
export type DisplayCurrency = "USD" | "SGD" | "GBP";

const KEYS = {
  theme: "custos.theme",
  currency: "custos.currency",
  vaultAutoLock: "custos.vaultAutoLock",
  hideBalances: "custos.hideBalances",
};

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked — the choice simply isn't remembered.
  }
}

export function resolveTheme(pref: ThemePref): "dark" | "light" {
  if (pref !== "system") return pref;
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function applyTheme(pref: ThemePref): void {
  const theme = resolveTheme(pref);
  document.documentElement.dataset.theme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", theme === "light" ? "#F3F1F8" : "#08070C");
}

const savedTheme = read(KEYS.theme);
const savedCurrency = read(KEYS.currency);
const savedAutoLock = Number(read(KEYS.vaultAutoLock));

interface PrefsState {
  theme: ThemePref;
  currency: DisplayCurrency;
  setTheme: (theme: ThemePref) => void;
  setCurrency: (currency: DisplayCurrency) => void;
  /** Vault auto-lock after this many minutes without activity (D61). */
  vaultAutoLockMinutes: AutoLockMinutes;
  setVaultAutoLockMinutes: (minutes: AutoLockMinutes) => void;
  /** Show •••• instead of amounts (privacy in public, D80). */
  hideBalances: boolean;
  setHideBalances: (hide: boolean) => void;
}

export const usePrefs = create<PrefsState>((set) => ({
  // Dark by default (D49); "system" follows the operating system.
  theme: savedTheme === "light" || savedTheme === "system" ? savedTheme : "dark",
  currency: savedCurrency === "SGD" || savedCurrency === "GBP" ? savedCurrency : "USD",
  setTheme: (theme) => {
    write(KEYS.theme, theme);
    applyTheme(theme);
    set({ theme });
  },
  setCurrency: (currency) => {
    write(KEYS.currency, currency);
    set({ currency });
  },
  vaultAutoLockMinutes: isAutoLockMinutes(savedAutoLock) ? savedAutoLock : DEFAULT_AUTO_LOCK_MINUTES,
  setVaultAutoLockMinutes: (minutes) => {
    write(KEYS.vaultAutoLock, String(minutes));
    set({ vaultAutoLockMinutes: minutes });
  },
  hideBalances: read(KEYS.hideBalances) === "1",
  setHideBalances: (hide) => {
    write(KEYS.hideBalances, hide ? "1" : "0");
    set({ hideBalances: hide });
  },
}));

/** Follow OS changes while the "system" theme is selected. */
export function watchSystemTheme(): () => void {
  const mq = window.matchMedia?.("(prefers-color-scheme: light)");
  if (!mq) return () => {};
  const onChange = () => {
    if (usePrefs.getState().theme === "system") applyTheme("system");
  };
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

/** "••••" instead of an amount while balances are hidden (D80). */
export const HIDDEN_AMOUNT = "••••";
export function useMask(): (shown: string) => string {
  const hide = usePrefs((s) => s.hideBalances);
  return (shown) => (hide ? HIDDEN_AMOUNT : shown);
}
